import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthPageShell, Button, RrnInput } from '@/components';
import { useAuthFetch } from '@/hooks';
import { validateRRN, formatRRN } from '@/utils';

import { ROUTES } from '../routes.ts';
import type { UserProfileResponse, UserProfileRequest } from '@dermalink/shared';
import styles from './Profile.module.css';

type RedirectReason = 'missing_info' | 'no_consent' | 'email_exists';

class ConflictError extends Error {
  constructor(public email: string | null) { super(); }
}

export function Profile() {
  const location    = useLocation();
  const navigate    = useNavigate();
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  const redirectState  = location.state as { reason?: RedirectReason; email?: string | null } | null;
  const reason         = redirectState?.reason ?? null;
  const existingEmail  = redirectState?.email ?? null;

  // Form state
  const [firstName,  setFirstName]  = useState('');
  const [lastName,   setLastName]   = useState('');
  const [rrn,        setRrn]        = useState('');
  const [email,      setEmail]      = useState('');
  const [consent,    setConsent]    = useState(false);

  // UI state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [conflict,    setConflict]    = useState<{ email: string | null } | null>(null);

  const { isPending: loadPending, isError: loadFailed, data: profileData } = useQuery<UserProfileResponse | null>({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authFetch('/profile');
      if (res.status === 401) { navigate(ROUTES.login, { replace: true }); return null; }
      if (res.status === 404) return null;
      if (res.status === 409) return null;
      if (!res.ok) throw new Error();
      return (await res.json()) as UserProfileResponse;
    },
  });

  // Initialize form fields when profile data arrives (or from cache on re-visit)
  useEffect(() => {
    if (!profileData) return;
    setFirstName(profileData.first_name ?? '');
    setLastName(profileData.last_name ?? '');
    setRrn(formatRRN(profileData.rrn ?? ''));
    setConsent(profileData.consent);
    setEmail((prev) => profileData.email || prev);
  }, [profileData]);

  // Cognito email fallback for new users (no profile row yet)
  useEffect(() => {
    fetchAuthSession().then((session) => {
      const cognitoEmail = (session.tokens?.idToken?.payload?.email as string) ?? '';
      setEmail((prev) => prev || cognitoEmail);
    }).catch(() => {});
  }, []);

  const saveMutation = useMutation({
    mutationFn: async (body: UserProfileRequest) => {
      const res = await authFetch('/profile', {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      if (res.status === 401) { navigate(ROUTES.login, { replace: true }); throw new Error('unauthorized'); }
      if (res.status === 409) {
        const errBody = await res.json().catch(() => null) as { email?: string | null } | null;
        throw new ConflictError(errBody?.email ?? null);
      }
      if (res.ok) return (await res.json()) as UserProfileResponse;
      let serverMessage: string | null = null;
      try {
        const errBody = (await res.json()) as { message?: string };
        if (typeof errBody.message === 'string' && errBody.message.trim()) serverMessage = errBody.message.trim();
      } catch { /* ignore */ }
      throw new Error(serverMessage ?? 'Er is een fout opgetreden. Probeer het opnieuw.');
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UserProfileResponse | null>(['profile'], data);
      navigate('/', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ConflictError) { setConflict({ email: err.email }); return; }
      if (err.message && err.message !== 'unauthorized') {
        setFieldErrors((prev) => ({ ...prev, _submit: err.message }));
      }
    },
  });

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName  = 'Voornaam is verplicht.';
    if (!lastName.trim())  errors.lastName   = 'Achternaam is verplicht.';
    if (!validateRRN(rrn)) errors.rrn        = 'Ongeldig rijksregisternummer.';
    if (!consent)          errors.consent    = 'U dient akkoord te gaan om de app te gebruiken.';
    setFieldErrors(errors);
    const keys = Object.keys(errors);
    if (keys.length > 0) {
      const order = ['firstName', 'lastName', 'rrn', 'consent'] as const;
      const first = order.find((k) => errors[k]);
      if (first) {
        const id = first === 'consent' ? 'consent-checkbox' : first;
        queueMicrotask(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
    return keys.length === 0;
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validate()) return;
    setFieldErrors((prev) => { const { _submit, ...rest } = prev; void _submit; return rest; });
    setConflict(null);
    saveMutation.mutate({
      first_name: firstName.trim(),
      last_name:  lastName.trim(),
      rrn:        rrn.replace(/\D/g, ''),
      consent,
    });
  }

  if (loadPending) {
    return (
      <AuthPageShell maxWidth="640px" hideNav={!!reason}>
        <p className="text-muted status-message">Laden&hellip;</p>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell maxWidth="640px" hideNav={!!reason}>

      <h1 className={`page-heading ${styles.heading}`}>{consent ? 'Mijn profiel' : 'Bevestiging van gegevens'}</h1>

        {/* Conditional banners based on redirect reason */}
        {reason === 'no_consent' && (
          <div className={styles.bannerWarning} role="alert">
            Om de app te kunnen gebruiken, dient u akkoord te gaan met de voorwaarden hieronder.
          </div>
        )}
        {reason === 'email_exists' && (
          <div className={styles.bannerError} role="alert">
            <p>
              Er bestaat al een account voor
              {existingEmail ? <> <strong>{existingEmail}</strong></> : ' dit e-mailadres'}, aangemaakt
              via een andere aanmeldmethode (bijv. e-mail/wachtwoord in plaats van Google, of
              omgekeerd).
            </p>
            <p>
              Meld u af en meld u opnieuw aan met de methode waarmee u dit account oorspronkelijk
              heeft aangemaakt.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={async () => { await signOut(); navigate(ROUTES.login, { replace: true }); }}
            >
              Afmelden
            </Button>
          </div>
        )}
        {loadFailed && (
          <div className={styles.bannerError} role="alert">
            Uw profielgegevens konden niet worden geladen. Probeer de pagina te vernieuwen.
          </div>
        )}
        {saveMutation.isError && (
          <div className={styles.bannerError} role="alert">
            {fieldErrors._submit ?? 'Er is een fout opgetreden. Probeer het opnieuw.'}
          </div>
        )}
        {conflict && (
          <div className={styles.bannerError} role="alert">
            <p>
              Dit rijksregisternummer is al gekoppeld aan een ander account
              {conflict.email && <> (<strong>{conflict.email}</strong>)</>}. Waarschijnlijk heeft u
              zich eerder aangemeld via een andere methode (bijv. e-mail/wachtwoord of een social
              login).
            </p>
            <p>
              Meld u af en gebruik de aanmeldmethode waarmee u uw account oorspronkelijk heeft
              aangemaakt.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={async () => { await signOut(); navigate(ROUTES.login, { replace: true }); }}
            >
              Afmelden
            </Button>
          </div>
        )}

        {reason !== 'email_exists' && (
        <form onSubmit={handleSubmit} noValidate className={styles.form}>

          {/* Voornaam */}
          <div className="field-group">
            <label htmlFor="firstName" className="field-label">Voornaam</label>
            <input
              id="firstName"
              type="text"
              className={fieldErrors.firstName ? 'field-input field-input-error' : 'field-input'}
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
            {fieldErrors.firstName && (
              <span className={styles.errorMsg}>{fieldErrors.firstName}</span>
            )}
          </div>

          {/* Achternaam */}
          <div className="field-group">
            <label htmlFor="lastName" className="field-label">Achternaam</label>
            <input
              id="lastName"
              type="text"
              className={fieldErrors.lastName ? 'field-input field-input-error' : 'field-input'}
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              autoComplete="family-name"
            />
            {fieldErrors.lastName && (
              <span className={styles.errorMsg}>{fieldErrors.lastName}</span>
            )}
          </div>

          {/* Rijksregisternummer */}
          <RrnInput
            id="rrn"
            value={rrn}
            onChange={setRrn}
            error={fieldErrors.rrn}
            autoComplete="off"
          />

          {/* E-mailadres (readonly) */}
          <div className="field-group">
            <label htmlFor="email" className="field-label">
              E-mailadres
              <span className={`text-muted ${styles.labelHint}`}>(niet wijzigbaar)</span>
            </label>
            <input
              id="email"
              type="email"
              className={`field-input ${styles.inputReadonly}`}
              value={email}
              readOnly
            />
          </div>

          {/* Legal warning + consent checkbox */}
          <div className={styles.legalBlock}>
            <p className={styles.legalText}>
              <strong>Verwerking van uw persoonsgegevens</strong>
            </p>
            <p className={styles.legalText}>
              Dermalink BV verwerkt uw rijksregisternummer om u als patiënt correct te identificeren
              in het kader van een teleconsultatie. Dit gegeven wordt niet gebruikt voor andere
              doeleinden en niet gedeeld met derden buiten de betrokken zorgverlener.
            </p>
            <p className={styles.legalText}>
              De verwerking is gebaseerd op uw uitdrukkelijke toestemming. U kunt deze toestemming
              ten allen tijde intrekken, wat de verdere verwerking van uw gegevens stopzet maar geen
              terugwerkende kracht heeft.
            </p>
            <p className={styles.legalText}>
              Uw gegevens worden bewaard gedurende 7 jaar na uw laatste activiteit op het Platform.
              Voor vragen of verzoeken:{' '}
              <a href="mailto:privacy@dermalink.be">privacy@dermalink.be</a>.
            </p>
            <p className={styles.legalText}>
              U heeft het recht op inzage, correctie en verwijdering van uw gegevens. Meer
              informatie vindt u in onze privacyverklaring.
            </p>

            <label className={styles.consentLabel}>
              <input
                id="consent-checkbox"
                type="checkbox"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
                className={styles.consentCheckbox}
              />
              <span>
                Ik heb kennisgenomen van de verwerking van mijn persoonsgegevens en geef toestemming
                voor de verwerking van mijn rijksregisternummer.
              </span>
            </label>
            {fieldErrors.consent && (
              <span className={styles.errorMsg}>{fieldErrors.consent}</span>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </form>
        )}

        {/* GDPR section */}
        <section className={styles.gdprSection} aria-labelledby="gdpr-heading">
          <h2 id="gdpr-heading" className={styles.gdprHeading}>
            Privacy &amp; gegevensbescherming
          </h2>
          <p className={`text-muted ${styles.gdprText}`}>
            Uw persoonsgegevens worden verwerkt in overeenstemming met de Algemene Verordening
            Gegevensbescherming (AVG/GDPR). U heeft het recht op inzage, rectificatie en
            verwijdering van uw gegevens. Voor vragen of verzoeken kunt u contact opnemen via{' '}
            <a href="mailto:privacy@dermalink.be">privacy@dermalink.be</a>.
          </p>
        </section>
    </AuthPageShell>
  );
}
